<template>
  <div class="chatbot-container" style="--main-background: #e9eef6; --main-text-color: #1f1f1f;">
    <OverviewChatMessages
      v-if="!isEmptyMessages"
      :messages="displayMessages"
      :collapsed="overviewCollapsed"
      @toggle-collapse="overviewCollapsed = !overviewCollapsed"
      @select="scrollToMessage"
    />

    <div class="chatbot-main">
      <div
        ref="listWrapper"
        class="chatbot-container-list-wrapper"
        :class="{ 'chatbot-container-list-wrapper--empty': isEmptyMessages }"
        @scroll.passive="onScroll"
      >
        <div
          ref="listContent"
          class="chatbot-container-list pa-4"
          :class="{ 'chatbot-container-list--empty': isEmptyMessages }"
        >
          <template v-if="isEmptyMessages">
            <div class="chatbot-empty-center">
              <div class="chatbot-container-empty-message">Where should we start?</div>
              <div class="chatbot-empty-center__block chatbot-empty-center__composer">
                <ChatbotComposer
                  ref="composer"
                  v-model="draft"
                  :disabled="isComposerDisabled"
                  :provider-id="selectedProviderId"
                  :model-id="selectedModelId"
                  @send="onSendMessage"
                  @apply-prompt-template="onApplyPromptTemplate"
                  @open-image-generation="onOpenImageGenerationFromConcept"
                  @model-change="onModelChange"
                  :product="product"
                  @scroll-to-bottom="scrollToBottomSmooth"
                  @pause-send-message="onPauseSendMessage"
                  :show-pause-button="canShowPauseButton"
                  :loading-pause-send-message="loadingPauseSendMessage"
                />
              </div>
            </div>
          </template>

          <template v-else>
            <div
              v-for="(msg, msgIndex) in displayMessages"
              :key="msg._key"
              ref="msgAnchors"
              :data-msg-key="msg._key || msg._id"
              class="chat-message-anchor"
            >
              <ChatbotMessageItem
                :message="msg"
                :product="product"
                :is-last-message="msgIndex === displayMessages.length - 1"
                :force-ad-image-gen="isForceAdImageGen(msg)"
                @retry="onRetryLocalMessage"
                @delete="onDeleteLocalMessage"
                @apply-prompt-template="onApplyPromptTemplate"
                @open-image-generation="onOpenImageGenerationFromConcept"
                @open-preview="onOpenPreview"
              />
            </div>
            <div ref="scrollAnchor" class="chatbot-scroll-anchor" tabindex="-1" />
          </template>
        </div>
      </div>
      <div v-if="!isEmptyMessages" class="chatbot-bottom" :class="{ 'chatbot-bottom--slide-down': composerSlideDown }">
        <ChatbotComposer
          ref="composer"
          v-model="draft"
          :disabled="isComposerDisabled"
          :provider-id="selectedProviderId"
          :model-id="selectedModelId"
          @send="onSendMessage"
          @apply-prompt-template="onApplyPromptTemplate"
          @open-image-generation="onOpenImageGenerationFromConcept"
          @model-change="onModelChange"
          :product="product"
          @scroll-to-bottom="scrollToBottomSmooth"
          @pause-send-message="onPauseSendMessage"
          :show-pause-button="canShowPauseButton"
          :loading-pause-send-message="loadingPauseSendMessage"
        />
      </div>
    </div>

    <div v-if="messagesLoading" class="chatbot-loading">
      <div style="width: 100%; height: 100%" class="d-flex justify-center align-center">
        <page-loader />
      </div>
    </div>

    <PreviewImage
      v-if="previewImage.src"
      :src="previewImage.src"
      :show-preview="previewImage.show"
      @close="onClosePreviewImage"
    />
    <PreviewVideo
      v-if="previewVideo.src"
      :src="previewVideo.src"
      :show-preview="previewVideo.show"
      @close="onClosePreviewVideo"
    />
  </div>
</template>

<script>
import ChatbotMessageItem from './ChatbotMessageItem.vue';
import ChatbotComposer from './ChatbotComposer.vue';
import OverviewChatMessages from './OverviewChatMessages.vue';
import { DEFAULT_PROVIDER_ID, DEFAULT_MODEL_ID } from './utils/ai-providers';
import { chatbotApi } from '@/apis/chatbot';
import { sendMessageStream } from './utils/streams.js';
import event from '@/plugins/event-bus';
import PreviewImage from './PreviewImage.vue';
import PreviewVideo from './PreviewVideo.vue';
import { isVideoUrl } from './utils';

const PROCESSING_POLL_INTERVAL_MS = 30000;
const GENERATE_AD_IMAGE_POLL_INTERVAL_MS = 60000;
const GENERATE_AD_IMAGE_INTENT = 'GENERATE_AD_IMAGE';
const SCROLL_PIN_THRESHOLD_PX = 80;
const SCROLL_MESSAGE_TOP_PADDING_PX = 12;

export default {
  name: 'ChatbotContainer',
  components: { ChatbotMessageItem, ChatbotComposer, OverviewChatMessages, PreviewImage, PreviewVideo },
  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    product: {
      type: Object,
      default: () => ({}),
    },
    messagesLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draft: '',
      isAwaitingBot: false,
      scrollPinnedToBottom: true,
      overviewCollapsed: false,
      selectedProviderId: DEFAULT_PROVIDER_ID,
      selectedModelId: DEFAULT_MODEL_ID,
      composerSlideDown: false,
      composerSlideTimer: null,
      resizeObserver: null,
      localMessages: [],
      processingLocalKey: null,
      processingPollTimer: null,
      processingPollIntervalMs: PROCESSING_POLL_INTERVAL_MS,
      hasCompletedInitialLoad: false,
      adImageGenProcessingKey: null,
      previewImage: {
        src: null,
        show: false,
      },
      previewVideo: {
        src: null,
        show: false,
      },
      loadingPauseSendMessage: false,
    };
  },
  computed: {
    isEmptyMessages() {
      return (this.displayMessages || []).length === 0;
    },
    normalizedMessages() {
      return Array.isArray(this.messages) ? this.messages : [];
    },
    localIdSet() {
      const set = new Set();
      const local = Array.isArray(this.localMessages) ? this.localMessages : [];
      for (const m of local) {
        const id = this.getMessageId(m);
        if (id) set.add(id);
      }
      return set;
    },
    displayMessages() {
      const server = this.normalizedMessages;
      const local = Array.isArray(this.localMessages) ? this.localMessages : [];
      if (!local.length) return server;
      const ids = this.localIdSet;
      const filtered = ids.size
        ? server.filter(s => {
            const id = this.getMessageId(s);
            return !id || !ids.has(id);
          })
        : server;
      return [...filtered, ...local];
    },
    isAiMessageProcessing() {
      return (this.displayMessages || []).some(m => this.isMessageAiProcessing(m));
    },
    isComposerDisabled() {
      return this.isAwaitingBot || this.isAiMessageProcessing;
    },
    lastPausableMessage() {
      const messages = this.displayMessages || [];
      if (!messages.length) return null;
      const last = messages[messages.length - 1];
      if (!last || String(last.status || '').trim() !== 'processing') return null;
      return this.getMessageId(last) ? last : null;
    },
    processingMessageIdForPause() {
      const last = this.lastPausableMessage;
      return last ? this.getMessageId(last) : null;
    },
    canShowPauseButton() {
      return this.processingMessageIdForPause != null;
    },
  },
  watch: {
    messages: {
      handler() {
        this.syncLocalMessagesWithServer();
        this.$nextTick(() => this.applyScrollAfterListChange());
      },
      deep: true,
    },
    localMessages: {
      handler() {
        this.$nextTick(() => this.tryScrollToBottomIfPinned());
      },
      deep: true,
    },
    displayMessages: {
      handler() {
        this.syncProcessingPoll();
      },
      deep: true,
    },
    isAwaitingBot() {
      this.$nextTick(() => this.tryScrollToBottomIfPinned());
    },
    messagesLoading(loading) {
      if (loading) return;
      this.syncLocalMessagesWithServer();

      if (!this.hasCompletedInitialLoad) {
        this.hasCompletedInitialLoad = true;
        this.runInitialScrollToBottom();
        return;
      }

      this.$nextTick(() => this.applyScrollAfterListChange());
    },
  },
  mounted() {
    if (this.isMobile()) {
      this.overviewCollapsed = true;
    }

    this.$nextTick(() => {
      this.scrollToBottomInstant();
      requestAnimationFrame(() => this.scrollToBottomInstant());
      this.setupListResizeObserver();
    });

    this.syncProcessingPoll();
    this._boundBeforeUnload = event => this.handleBeforeUnload(event);
    window.addEventListener('beforeunload', this._boundBeforeUnload);
  },
  methods: {
    onOpenPreview(att) {
      if (!att || !att.fullUrl) return;
      if (isVideoUrl(att.fullUrl)) {
        this.previewVideo.src = att.fullUrl;
        this.previewVideo.show = true;
      } else {
        this.previewImage.src = att.fullUrl;
        this.previewImage.show = true;
      }
    },
    onClosePreviewImage() {
      this.previewImage.show = false;
    },
    onClosePreviewVideo() {
      this.previewVideo.show = false;
    },
    isMobile() {
      return window?.innerWidth < 768;
    },
    onApplyPromptTemplate(pt) {
      const normalized = pt.replace(/^\s+|\s+$/g, '');
      if (!normalized) return;

      this.draft = normalized;
    },
    onOpenImageGenerationFromConcept(concept) {
      const composer = this.$refs.composer;
      if (composer && typeof composer.openImageGenerationWithConcept === 'function') {
        composer.openImageGenerationWithConcept(concept);
      }
    },
    getListWrapper() {
      return this.$refs.listWrapper || null;
    },
    onScroll(e) {
      const el = e.target;
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      this.scrollPinnedToBottom = dist < SCROLL_PIN_THRESHOLD_PX;
      event.$emit('chatbot:scroll');
    },
    scrollToBottom({ instant = false } = {}) {
      const wrapper = this.getListWrapper();
      if (!wrapper) return;
      const behavior = instant ? 'auto' : 'smooth';
      const top = this.isEmptyMessages ? 0 : wrapper.scrollHeight;
      wrapper.scrollTo({ top, behavior });
      // Pin synchronously: lazy images can inflate scrollHeight before onScroll runs.
      this.scrollPinnedToBottom = true;
    },
    scrollToBottomInstant() {
      this.scrollToBottom({ instant: true });
    },
    scrollToBottomSmooth() {
      this.scrollToBottom({ instant: false });
    },
    tryScrollToBottomIfPinned() {
      if (this.scrollPinnedToBottom) this.scrollToBottomInstant();
    },
    isAdImageGenerationInProgress() {
      return this.getLastDisplayMessageIntent() === GENERATE_AD_IMAGE_INTENT && this.hasProcessingMessages();
    },
    applyScrollAfterListChange() {
      if (!this.hasCompletedInitialLoad) return;

      if (this.isAdImageGenerationInProgress()) {
        this.scrollPinnedToBottom = true;
        this.$nextTick(() => {
          this.scrollToBottomInstant();
          requestAnimationFrame(() => this.scrollToBottomInstant());
        });
        return;
      }

      this.tryScrollToBottomIfPinned();
    },
    runInitialScrollToBottom() {
      this.scrollPinnedToBottom = true;
      this.$nextTick(() => {
        this.scrollToBottomInstant();
        requestAnimationFrame(() => this.scrollToBottomInstant());
        this.bindScrollAfterProcessingImages();
      });
    },
    bindScrollAfterProcessingImages() {
      const wrapper = this.getListWrapper();
      if (!wrapper) return;
      wrapper.querySelectorAll('img').forEach(img => {
        if (img.complete) return;
        const onDone = () => {
          img.removeEventListener('load', onDone);
          img.removeEventListener('error', onDone);
          if (this.scrollPinnedToBottom) this.scrollToBottomInstant();
        };
        img.addEventListener('load', onDone);
        img.addEventListener('error', onDone);
      });
    },
    setupListResizeObserver() {
      const wrapper = this.$refs.listWrapper;
      const listContent = this.$refs.listContent;
      if (typeof window === 'undefined' || !('ResizeObserver' in window) || (!wrapper && !listContent)) {
        return;
      }
      this.resizeObserver = new ResizeObserver(() => this.tryScrollToBottomIfPinned());
      if (wrapper) this.resizeObserver.observe(wrapper);
      if (listContent) this.resizeObserver.observe(listContent);
    },
    scrollToMessage(msgId, { instant = false } = {}) {
      const wrapper = this.getListWrapper();
      if (!wrapper || !msgId) return;

      const el = wrapper.querySelector(`.chat-message-anchor[data-msg-key="${CSS.escape(String(msgId))}"]`);
      if (!el) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const targetTop = wrapper.scrollTop + (elRect.top - wrapperRect.top) - SCROLL_MESSAGE_TOP_PADDING_PX;
      wrapper.scrollTo({
        top: Math.max(0, targetTop),
        behavior: instant ? 'auto' : 'smooth',
      });
    },
    finishSendMessageScroll(msg) {
      this.$nextTick(() => {
        this.tryScrollToBottomIfPinned();
        if (msg._localStatus === 'done') {
          const composer = this.$refs.composer;
          if (composer && typeof composer.focusTextarea === 'function') composer.focusTextarea();
        }
      });
    },
    onModelChange({ providerId, modelId }) {
      this.selectedProviderId = providerId;
      this.selectedModelId = modelId;
    },
    createLocalKey() {
      return `local:${Date.now()}:${Math.random()
        .toString(16)
        .slice(2)}`;
    },
    getMessageId(msg) {
      if (!msg) return null;
      const id = msg._id != null ? msg._id : msg.id;
      return id != null ? String(id) : null;
    },
    findServerMatchForLocal(local, serverList) {
      if (!local || !Array.isArray(serverList) || !serverList.length) return null;

      const localId = this.getMessageId(local);
      if (localId) {
        const byId = serverList.find(s => this.getMessageId(s) === localId);
        if (byId) return byId;
        return null;
      }

      // Fallback by userMessage when local has no server id yet
      // (e.g. streaming messages that completed without receiving a prompt_id).
      const text = String(local.userMessage || '').trim();
      if (!text) return null;

      for (let i = serverList.length - 1; i >= 0; i -= 1) {
        const serverMsg = serverList[i];
        if (!serverMsg) continue;
        if (String(serverMsg.userMessage || '').trim() === text) return serverMsg;
      }

      return null;
    },
    applyServerDataToLocal(local, server) {
      if (!local || !server) return;

      const serverId = this.getMessageId(server);
      if (serverId) {
        this.$set(local, '_id', serverId);
      }

      const reply = server.aiReply != null ? server.aiReply : server.reply;
      if (reply != null) this.$set(local, 'aiReply', String(reply));

      if (server.status != null) {
        const status = String(server.status);
        this.$set(local, 'status', status);
        if (status === 'failed' && local._key === this.adImageGenProcessingKey) {
          this.adImageGenProcessingKey = null;
        }
      }

      if (Array.isArray(server.attachments_output)) {
        this.$set(
          local,
          'attachments_output',
          server.attachments_output.map(u => (u == null ? '' : String(u).trim())).filter(Boolean),
        );
      }

      if (server.intent != null) this.$set(local, 'intent', String(server.intent));

      if (server.createdAt != null) this.$set(local, 'createdAt', server.createdAt);
    },
    syncLocalMessagesWithServer() {
      const server = this.normalizedMessages;
      if (!this.localMessages.length || !server.length) return;

      const nextLocal = [];

      for (const local of this.localMessages) {
        if (!local || !local._localOnly) {
          nextLocal.push(local);
          continue;
        }

        const match = this.findServerMatchForLocal(local, server);
        if (!match) {
          nextLocal.push(local);
          continue;
        }

        this.applyServerDataToLocal(local, match);

        const matchStatus = String(match.status || '')
          .trim()
          .toLowerCase();
        const localStatus = String(local.status || '')
          .trim()
          .toLowerCase();
        if (matchStatus === 'processing' || localStatus === 'processing') {
          nextLocal.push(local);
          continue;
        }

        // Server is done/failed — remove local, UI get server version via displayMessages.
      }

      this.localMessages = nextLocal;
    },
    buildLocalMessage({ key, text, attachments, image_ref, promptType, mode }) {
      const safeAttachments = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
      const safeImageRef = Array.isArray(image_ref) ? image_ref.filter(Boolean) : [];
      const resolvedMode = mode === 'edit' || mode === 'redesign' || mode === 'concept' ? mode : null;
      return {
        _key: key,
        _id: null,
        _sessionId: null,
        _localOnly: true,
        _localStatus: 'processing',
        _localError: null,
        _localPayload: {
          text,
          attachments: safeAttachments,
          image_ref: safeImageRef,
          mode: resolvedMode,
        },
        userMessage: text,
        attachments: safeAttachments,
        image_ref: safeImageRef,
        aiReply: '',
        attachments_output: [],
        intent: '',
        status: '',
        promptType: promptType || 'normal_chat',
        mode: resolvedMode,
        createdAt: new Date().toISOString(),
      };
    },

    clearComposerMode() {
      const composer = this.$refs.composer;
      if (composer && typeof composer.clearComposerMode === 'function') {
        composer.clearComposerMode();
      }
    },

    async sendLocalMessage(localMsg) {
      if (!this.product._id) return;
      if (!localMsg) return;

      const rawAttachments = Array.isArray(localMsg._localPayload?.attachments)
        ? localMsg._localPayload.attachments
        : Array.isArray(localMsg.attachments)
        ? localMsg.attachments
        : [];
      const rawImageRef = Array.isArray(localMsg._localPayload?.image_ref)
        ? localMsg._localPayload.image_ref
        : Array.isArray(localMsg.image_ref)
        ? localMsg.image_ref
        : [];
      const safeAttachments = rawAttachments.filter(Boolean);
      const safeImageRef = rawImageRef.filter(Boolean);
      const attachmentUrls = safeAttachments.map(a => (typeof a === 'string' ? a : a?.src)).filter(Boolean);
      const imageRefUrls = safeImageRef.map(a => (typeof a === 'string' ? a : a?.src)).filter(Boolean);
      const hasAttachments = attachmentUrls.length > 0;
      const hasImageRef = imageRefUrls.length > 0;

      const messageText = localMsg._localPayload?.text || localMsg.userMessage || '';
      if (!messageText && !hasAttachments && !hasImageRef) return;

      const payload = {
        productId: this.product._id,
        message: messageText,
        modelAI: this.selectedModelId,
        prompt_type: localMsg.promptType || 'normal_chat',
        ...(hasAttachments ? { attachments: attachmentUrls } : {}),
        ...(hasImageRef ? { image_ref: imageRefUrls } : {}),
        ...(localMsg.mode ? { mode: localMsg.mode } : {}),
      };

      const adImageGenerationModes = ['edit', 'redesign', 'concept'];
      // Ad image generation flow
      if (adImageGenerationModes.includes(localMsg.mode) || localMsg.promptType === 'template') {
        try {
          this.isAwaitingBot = true;
          this.processingLocalKey = localMsg._key;
          localMsg._localStatus = 'processing';
          localMsg._localError = null;
          localMsg.aiReply = '';
          localMsg.attachments_output = [];
          localMsg.intent = '';

          // Use the GPT model to create advertising images.
          const payloadForGenImage = {
            ...payload,
            modelAI: 'gpt',
          };
          const { data } = await chatbotApi.sendMessageWithAdsContent(payloadForGenImage);

          if (!data.success) {
            this.$store.commit('setMessages', {
              type: 'error',
              messages: data?.message || 'Failed to send message!',
            });
            localMsg._localStatus = 'failed';
            this.$set(localMsg, 'status', 'failed');
          } else {
            const dataValue = data?.data && typeof data.data === 'object' ? data.data : {};
            const msgId = dataValue.prompt_id != null ? dataValue.prompt_id : null;
            const aiReply = dataValue.reply != null ? String(dataValue.reply) : '';
            const attachmentsOut = Array.isArray(dataValue.attachments_output)
              ? dataValue.attachments_output.map(u => (u == null ? '' : String(u).trim())).filter(Boolean)
              : [];
            const status = dataValue.status != null ? String(dataValue.status) : '';
            const intent = dataValue.intent != null ? String(dataValue.intent) : '';

            this.$set(localMsg, '_id', msgId);
            this.$set(localMsg, 'aiReply', aiReply);
            this.$set(localMsg, 'attachments_output', attachmentsOut);
            this.$set(localMsg, 'status', status);
            this.$set(localMsg, 'intent', intent);
            this.$set(localMsg, '_localStatus', 'done');

            if (String(status) === 'processing' && intent.trim() === GENERATE_AD_IMAGE_INTENT) {
              this.adImageGenProcessingKey = localMsg._key;
            } else if (String(status) !== 'processing') {
              this.adImageGenProcessingKey = null;
            }
            this.clearComposerMode();
          }
        } catch (error) {
          console.error('Error while sending message:', error);
          localMsg._localStatus = 'failed';
          localMsg.status = 'failed';
          localMsg._localError = error;
          this.$store.commit('setMessages', {
            type: 'error',
            messages:
              error?.response?.data?.message || error?.data?.message || error?.message || 'Failed to send message!',
          });
        } finally {
          this.isAwaitingBot = false;
          this.processingLocalKey = null;
          if (localMsg.status !== 'processing') this.adImageGenProcessingKey = null;
        }
      } else {
        // Normal chat flow
        try {
          this.isAwaitingBot = true;
          this.processingLocalKey = localMsg._key;
          localMsg._localStatus = 'processing';
          localMsg._localError = null;
          localMsg.aiReply = '';
          localMsg.attachments_output = [];
          localMsg.intent = '';

          // Use the DEEPSEEK model to create advertising content.
          const payloadForGenContent = {
            ...payload,
            modelAI: 'deepseek',
          };

          await sendMessageStream(payloadForGenContent, {
            onSession: event => {
              const { session_id, prompt_id } = event;
              if (session_id) {
                this.$set(localMsg, '_sessionId', session_id);
              }
              if (prompt_id) {
                this.$set(localMsg, '_id', String(prompt_id));
              }
            },
            onDelta: (_chunk, fullText) => {
              this.$set(localMsg, 'aiReply', fullText);
              // Only for text streaming: keep scrolling down as new delta arrives.
              this.$nextTick(() => {
                this.scrollPinnedToBottom = true;
                this.scrollToBottomInstant();
              });
            },
            onDone: () => {
              this.$set(localMsg, 'status', 'done');
              this.$set(localMsg, '_localStatus', 'done');
              this.clearComposerMode();
            },
            onError: event => {
              this.$set(localMsg, 'status', 'failed');
              this.$set(localMsg, '_localStatus', 'failed');
              this.$set(localMsg, '_localError', new Error(event.message));
            },
          });
        } catch (error) {
          console.error('Error while sending message:', error);
          localMsg._localStatus = 'failed';
          localMsg.status = 'failed';
          localMsg._localError = error;
          this.$store.commit('setMessages', {
            type: 'error',
            messages: error?.message || 'Failed to send message!',
          });
        } finally {
          this.isAwaitingBot = false;
          this.processingLocalKey = null;
        }
      }
    },
    async onSendMessage({ text, attachments, image_ref, promptType, mode }) {
      if (!this.product._id) {
        this.$store.commit('setMessages', {
          type: 'error',
          messages: 'Missing product id!',
        });
        return;
      }

      const normalizedText = (text || '').trim();
      const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
      const hasImageRef = Array.isArray(image_ref) && image_ref.length > 0;
      if (!normalizedText && !hasAttachments && !hasImageRef) return;
      const hasEmptyMessages = this.isEmptyMessages;

      if (this.processingLocalKey || this.isAiMessageProcessing) return;

      const localKey = this.createLocalKey();
      const msg = this.buildLocalMessage({
        key: localKey,
        text: normalizedText,
        attachments,
        image_ref,
        promptType,
        mode,
      });
      this.localMessages.push(msg);
      this.adImageGenProcessingKey =
        msg.mode === 'edit' || msg.mode === 'redesign' || msg.mode === 'concept' ? localKey : null;

      this.draft = '';
      this.$nextTick(() => this.scrollToBottomSmooth());

      await this.sendLocalMessage(msg);
      this.finishSendMessageScroll(msg);

      if (hasEmptyMessages) {
        this.$nextTick(() => {
          this.composerSlideDown = true;
          if (this.composerSlideTimer) clearTimeout(this.composerSlideTimer);
          this.composerSlideTimer = setTimeout(() => {
            this.composerSlideDown = false;
            this.composerSlideTimer = null;
          }, 280);
        });
      }
    },
    async onRetryLocalMessage(message) {
      if (!message) return;
      if (this.processingLocalKey) return;
      this.$nextTick(() => this.scrollToBottomSmooth());
      await this.sendLocalMessage(message);
      if (message._localStatus === 'done') {
        this.$nextTick(() => {
          const composer = this.$refs.composer;
          if (composer && typeof composer.focusTextarea === 'function') composer.focusTextarea();
        });
      }
      // When the user wants to retry the server message, fetch the data again
      if (!message._localOnly) {
        this.$emit('re-fetch-data');
      }
    },
    onDeleteLocalMessage(message) {
      if (!message || !message._localOnly) return;
      const idx = this.localMessages.findIndex(m => m && m._key === message._key);
      if (idx < 0) return;

      const deletingProcessing = this.processingLocalKey === message._key;
      this.localMessages.splice(idx, 1);
      if (deletingProcessing) {
        this.processingLocalKey = null;
        this.isAwaitingBot = false;
      }
    },
    isForceAdImageGen(msg) {
      if (!msg || msg._key !== this.adImageGenProcessingKey) return false;
      if (String(msg.status || '').trim() === 'failed') return false;
      if (msg._localOnly && msg._localStatus === 'failed') return false;
      return true;
    },
    isMessageAiProcessing(msg) {
      if (!msg || msg.status !== 'processing') return false;
      const hasAiText = String(msg.aiReply || '').trim().length > 0;
      const attachmentsOut = Array.isArray(msg.attachments_output) ? msg.attachments_output : [];
      const hasAiAttachments = attachmentsOut.some(u => u != null && String(u).trim().length > 0);
      return !hasAiText && !hasAiAttachments;
    },
    isMessageProcessingForUnload(msg) {
      if (!msg) return false;
      const status = String(msg.status || '')
        .trim()
        .toLowerCase();
      const localStatus = String(msg._localStatus || '')
        .trim()
        .toLowerCase();
      if (status === 'failed' || status === 'stopped' || localStatus === 'failed') return false;
      if (localStatus === 'processing') return true;
      return status === 'processing';
    },
    shouldBlockNavigation() {
      if (this.isAwaitingBot || this.processingLocalKey) return true;
      return (this.displayMessages || []).some(m => this.isMessageProcessingForUnload(m));
    },
    shouldBlockUnload() {
      return this.shouldBlockNavigation();
    },
    confirmLeaveWhileProcessing() {
      return window.confirm(
        'A message is still being processed. Are you sure you want to leave? Your progress may be lost.',
      );
    },
    handleBeforeUnload(event) {
      if (!this.shouldBlockUnload()) return;
      event.preventDefault();
      event.returnValue = ' ';
      return ' ';
    },
    hasProcessingMessages() {
      return (this.displayMessages || []).some(m => this.isMessageAiProcessing(m));
    },
    checkProcessingAndRefetch() {
      if (!this.hasProcessingMessages()) return;
      this.$emit('re-fetch-data');
    },
    getLastDisplayMessageIntent() {
      const messages = this.displayMessages || [];
      if (!messages.length) return '';
      const last = messages[messages.length - 1];
      return last?.intent != null ? String(last.intent).trim() : '';
    },
    getProcessingPollIntervalMs() {
      return GENERATE_AD_IMAGE_POLL_INTERVAL_MS;
    },
    syncProcessingPoll() {
      // Only poll when the last message is in ad-image generation flow.
      // Other flows should not refetch on an interval.
      if (this.getLastDisplayMessageIntent() !== GENERATE_AD_IMAGE_INTENT) {
        this.stopProcessingPoll();
        return;
      }

      const nextInterval = this.getProcessingPollIntervalMs();
      if (this.processingPollTimer != null && this.processingPollIntervalMs === nextInterval) return;
      this.processingPollIntervalMs = nextInterval;
      this.startProcessingPoll();
    },
    startProcessingPoll() {
      this.stopProcessingPoll();
      this.processingPollIntervalMs = this.getProcessingPollIntervalMs();

      const tick = () => {
        // Stop polling if the latest intent is no longer ad-image generation.
        if (this.getLastDisplayMessageIntent() !== GENERATE_AD_IMAGE_INTENT) {
          this.stopProcessingPoll();
          return;
        }
        this.checkProcessingAndRefetch();
        const delay = this.getProcessingPollIntervalMs();
        this.processingPollIntervalMs = delay;
        this.processingPollTimer = setTimeout(tick, delay);
      };

      this.processingPollTimer = setTimeout(tick, this.processingPollIntervalMs);
    },
    stopProcessingPoll() {
      if (this.processingPollTimer) {
        clearTimeout(this.processingPollTimer);
        this.processingPollTimer = null;
      }
    },
    async onPauseSendMessage() {
      if (!this.canShowPauseButton) return;
      const messageId = this.processingMessageIdForPause;
      if (messageId == null) return;

      this.loadingPauseSendMessage = true;
      try {
        await chatbotApi.updateChatMessageStatus(messageId, {
          status: 'stopped',
        });

        const processingLocal = this.localMessages.find(m => this.getMessageId(m) === String(messageId));
        if (processingLocal) {
          this.$set(processingLocal, 'status', 'stopped');
        }

        this.$emit('re-fetch-data');
        this.$nextTick(() => {
          const composer = this.$refs.composer;
          if (composer && typeof composer.focusTextarea === 'function') composer.focusTextarea();
        });
      } catch (error) {
        console.error('Error while pausing send message:', error);
        this.$store.commit('setMessages', {
          type: 'error',
          messages:
            error?.response?.data?.message || error?.data?.message || error?.message || 'Failed to stop message!',
        });
      } finally {
        this.loadingPauseSendMessage = false;
      }
    },
  },
  beforeDestroy() {
    if (this._boundBeforeUnload) {
      window.removeEventListener('beforeunload', this._boundBeforeUnload);
      this._boundBeforeUnload = null;
    }
    this.stopProcessingPoll();
    if (this.composerSlideTimer) clearTimeout(this.composerSlideTimer);
    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect();
      } catch (e) {
        // noop
      }
      this.resizeObserver = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.chatbot-container {
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: #ffffff;
  color: var(--main-text-color);
  position: relative;
}

.chatbot-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chatbot-container-empty-message {
  font-size: 28px;
  font-weight: 500;
  margin-top: 30%;
  margin-bottom: 64px;
}

.chatbot-empty-center {
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
}

.chatbot-empty-center__block {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.chatbot-empty-center__composer {
  text-align: left;
}

.chatbot-bottom {
  animation: none;
}

.chatbot-bottom--slide-down {
  animation: chatbot-slide-down 150ms ease-out both;
  will-change: transform, opacity;
}

@keyframes chatbot-slide-down {
  0% {
    transform: translateY(-10px);
    opacity: 0.6;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.chatbot-container-list-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.chatbot-container-list {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.chatbot-container-list--empty {
  /* height: 100%; */
  overflow: hidden;
}

.chatbot-container-list-wrapper--empty {
  overflow: hidden;
}

.chatbot-scroll-anchor {
  height: 1px;
  margin-bottom: 36px;
}

.typing-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}
.typing-row__avatar {
  background: var(--v-primary-base) !important;
  flex-shrink: 0;
}

@keyframes chatbot-dot {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.45;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chatbot-loading {
  position: absolute;
  inset: 0;
  padding: 16px;
  background: #ffffff;
  z-index: 5;
}
</style>

<style lang="scss">
.chatbot-container {
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 999px;
  }

  ::-webkit-scrollbar-thumb {
    background: #d0d1d3;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #c8cbd0;
  }
}
</style>
